import {
  ReportData,
  ReportFilters,
  ReportType,
  GenerateReportInput,
  ReportSummary,
  CategoryBreakdown,
  StatusBreakdown,
  MonthlyBreakdown,
  DepartmentBreakdown,
  ReportDetail
} from '../types/report';
import { Expense, ExpenseCategory, ExpenseStatus } from '../types/expense';
import { ExpenseService } from './expense';

/**
 * Service for handling report generation and analytics
 * Requirements: 7.1, 7.2, 7.3, 4.5 - Report generation and filtering
 */
export class ReportService {

  /**
   * Generates a report based on type and filters
   * Requirements: 7.1, 7.2, 7.3 - Different report types
   */
  static async generateReport(input: GenerateReportInput): Promise<ReportData> {
    try {
      // Get filtered expense data
      const expenses = await this.getFilteredExpenses(input.filters);
      
      // Generate report based on type
      const reportData: ReportData = {
        id: this.generateReportId(),
        type: input.type,
        title: this.generateReportTitle(input.type, input.filters),
        filters: input.filters,
        generatedAt: new Date().toISOString(),
        totalExpenses: expenses.length,
        totalAmount: expenses.reduce((sum, exp) => sum + exp.amount, 0),
        summary: await this.generateSummary(expenses, input.type),
        details: input.includeDetails ? await this.generateDetails(expenses) : []
      };

      return reportData;
    } catch (error) {
      console.error('Failed to generate report:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to generate report: ${error.message}` 
          : 'Failed to generate report'
      );
    }
  }

  /**
   * Gets filtered expenses based on report filters
   * Requirements: 4.5 - Multi-criteria filtering
   */
  private static async getFilteredExpenses(filters: ReportFilters): Promise<Expense[]> {
    /*
     * Get all expenses (this would be replaced with proper GraphQL query)
     *
     * The single-value `category`/`status` arguments are passed **only when exactly one value is
     * selected**, and are a narrowing hint rather than the filter — the authoritative pass is
     * client-side, below.
     *
     * They used to be `filters.categories?.[0]` and `filters.statuses?.[0]` unconditionally, and
     * with a multi-select that is not a hint but a wrong answer: asking for "Submitted and
     * Reimbursed" sent `status: 'Submitted'`, so the query came back holding no Reimbursed rows
     * at all and no amount of client-side filtering could put them back. The report then showed
     * a Submitted-only total under a heading naming both — the same last-value-wins failure the
     * chatbot's multi-status query had, and just as invisible, because a smaller number still
     * looks like a number.
     */
    const result = await ExpenseService.getAllExpenses({
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      department: filters.department,
      category: filters.categories?.length === 1 ? filters.categories[0] : undefined,
      status: filters.statuses?.length === 1 ? filters.statuses[0] : undefined
    });

    const allExpenses = result.items || [];

    // Apply additional client-side filters
    return allExpenses.filter((expense: Expense) => {
      // Employee filter
      if (filters.employeeId && expense.submitterId !== filters.employeeId) {
        return false;
      }

      /*
       * Categories and statuses, applied in full.
       *
       * Both are multi-select in the UI, and both were being handed to the query as
       * `filters.categories?.[0]` / `filters.statuses?.[0]` and then never checked again. Two
       * separate wrongs came out of that:
       *
       *  - Category was silently dropped altogether. ExpenseService.getAllExpenses does not send
       *    a `category` variable at all, so selecting "Travel" produced a report over every
       *    category — with a category breakdown that looked authoritative because it was
       *    correctly computed over the wrong set of rows.
       *  - Status kept only the first selection. Asking for "Submitted and Reimbursed" reported
       *    on Submitted alone, which reads as a real answer rather than a partial one. Same
       *    last-status-wins class of bug the chatbot's multi-status query had.
       *
       * Filtering here rather than widening the query keeps the fix in one place and correct
       * regardless of what the resolver supports; the server-side hint above still trims the
       * page it has to fetch.
       */
      if (filters.categories && filters.categories.length > 0 &&
          !filters.categories.includes(expense.category)) {
        return false;
      }
      if (filters.statuses && filters.statuses.length > 0 &&
          !filters.statuses.includes(expense.status)) {
        return false;
      }

      /*
       * Amount range filter.
       *
       * `!== undefined` rather than truthiness: a minAmount or maxAmount of 0 is a real bound
       * that the previous `if (filters.maxAmount && …)` skipped, so "expenses of at most £0"
       * returned every expense there is.
       */
      if (filters.minAmount !== undefined && expense.amount < filters.minAmount) {
        return false;
      }
      if (filters.maxAmount !== undefined && expense.amount > filters.maxAmount) {
        return false;
      }

      return true;
    });
  }

  /**
   * Generates report summary with breakdowns
   * Requirements: 7.1, 7.2, 7.3 - Report summaries and analysis
   */
  private static async generateSummary(expenses: Expense[], reportType: ReportType): Promise<ReportSummary> {
    const totalAmount = expenses.reduce((sum, exp) => sum + exp.amount, 0);
    const totalExpenses = expenses.length;
    const averageAmount = totalExpenses > 0 ? totalAmount / totalExpenses : 0;

    const summary: ReportSummary = {
      totalExpenses,
      totalAmount,
      averageAmount,
      categoryBreakdown: this.generateCategoryBreakdown(expenses, totalAmount),
      statusBreakdown: this.generateStatusBreakdown(expenses, totalAmount)
    };

    // Add type-specific breakdowns
    if (reportType === ReportType.MONTHLY) {
      summary.monthlyBreakdown = this.generateMonthlyBreakdown(expenses);
    }

    if (reportType === ReportType.DEPARTMENT) {
      summary.departmentBreakdown = await this.generateDepartmentBreakdown(expenses);
    }

    return summary;
  }

  /**
   * Generates category breakdown for spending analysis
   * Requirements: 7.1, 7.2, 7.3 - Category-wise analysis
   */
  private static generateCategoryBreakdown(expenses: Expense[], totalAmount: number): CategoryBreakdown[] {
    const categoryMap = new Map<ExpenseCategory, { count: number; amount: number }>();

    // Initialize all categories
    Object.values(ExpenseCategory).forEach(category => {
      categoryMap.set(category, { count: 0, amount: 0 });
    });

    // Aggregate by category
    expenses.forEach(expense => {
      const current = categoryMap.get(expense.category) || { count: 0, amount: 0 };
      categoryMap.set(expense.category, {
        count: current.count + 1,
        amount: current.amount + expense.amount
      });
    });

    // Convert to breakdown array
    return Array.from(categoryMap.entries())
      .map(([category, data]) => ({
        category,
        count: data.count,
        amount: data.amount,
        percentage: totalAmount > 0 ? (data.amount / totalAmount) * 100 : 0
      }))
      .sort((a, b) => b.amount - a.amount);
  }

  /**
   * Generates status breakdown for workflow analysis
   */
  private static generateStatusBreakdown(expenses: Expense[], totalAmount: number): StatusBreakdown[] {
    const statusMap = new Map<ExpenseStatus, { count: number; amount: number }>();

    // Initialize all statuses
    Object.values(ExpenseStatus).forEach(status => {
      statusMap.set(status, { count: 0, amount: 0 });
    });

    // Aggregate by status
    expenses.forEach(expense => {
      const current = statusMap.get(expense.status) || { count: 0, amount: 0 };
      statusMap.set(expense.status, {
        count: current.count + 1,
        amount: current.amount + expense.amount
      });
    });

    // Convert to breakdown array
    return Array.from(statusMap.entries())
      .map(([status, data]) => ({
        status,
        count: data.count,
        amount: data.amount,
        percentage: totalAmount > 0 ? (data.amount / totalAmount) * 100 : 0
      }))
      .sort((a, b) => b.amount - a.amount);
  }

  /**
   * Generates monthly breakdown for trend analysis
   * Requirements: 7.1 - Monthly reports
   */
  private static generateMonthlyBreakdown(expenses: Expense[]): MonthlyBreakdown[] {
    const monthMap = new Map<string, { count: number; amount: number }>();

    expenses.forEach(expense => {
      const month = expense.date.substring(0, 7); // YYYY-MM format
      const current = monthMap.get(month) || { count: 0, amount: 0 };
      monthMap.set(month, {
        count: current.count + 1,
        amount: current.amount + expense.amount
      });
    });

    return Array.from(monthMap.entries())
      .map(([month, data]) => ({
        month,
        count: data.count,
        amount: data.amount
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }

  /**
   * Generates department breakdown for organizational analysis
   * Requirements: 7.2 - Department reports
   */
  private static async generateDepartmentBreakdown(expenses: Expense[]): Promise<DepartmentBreakdown[]> {
    // This would typically fetch user data to get departments
    // For now, we'll simulate department data
    const departmentMap = new Map<string, { count: number; amount: number; employees: Set<string> }>();

    expenses.forEach(expense => {
      // In a real implementation, we'd look up the user's department
      const department = 'Engineering'; // Placeholder
      const current = departmentMap.get(department) || { count: 0, amount: 0, employees: new Set() };
      
      departmentMap.set(department, {
        count: current.count + 1,
        amount: current.amount + expense.amount,
        employees: current.employees.add(expense.submitterId)
      });
    });

    return Array.from(departmentMap.entries())
      .map(([department, data]) => ({
        department,
        count: data.count,
        amount: data.amount,
        employeeCount: data.employees.size
      }))
      .sort((a, b) => b.amount - a.amount);
  }

  /**
   * Generates detailed expense data for reports
   * Requirements: 7.3 - Employee reports with details
   */
  private static async generateDetails(expenses: Expense[]): Promise<ReportDetail[]> {
    return expenses.map(expense => ({
      expenseId: expense.id,
      title: expense.title,
      category: expense.category,
      amount: expense.amount,
      date: expense.date,
      status: expense.status,
      submitterName: 'Employee Name', // Would be fetched from user service
      submitterDepartment: 'Department', // Would be fetched from user service
      approvalDate: this.getApprovalDate(expense),
      reimbursementDate: expense.status === ExpenseStatus.REIMBURSED ? expense.updatedAt : undefined
    }));
  }

  /**
   * Gets approval date from expense approvals
   */
  private static getApprovalDate(expense: Expense): string | undefined {
    const approvedApproval = expense.approvals.find(
      approval => approval.status === 'Approved'
    );
    return approvedApproval?.timestamp;
  }

  /**
   * Generates unique report ID
   */
  private static generateReportId(): string {
    return `report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Generates report title based on type and filters
   */
  private static generateReportTitle(type: ReportType, filters: ReportFilters): string {
    const dateRange = this.formatDateRangeInternal(filters.dateFrom, filters.dateTo);
    
    switch (type) {
      case ReportType.MONTHLY:
        return `Monthly Expense Report${dateRange}`;
      case ReportType.DEPARTMENT:
        return `Department Expense Report${dateRange}${filters.department ? ` - ${filters.department}` : ''}`;
      case ReportType.EMPLOYEE:
        return `Employee Expense Report${dateRange}`;
      case ReportType.CATEGORY:
        return `Category-wise Expense Report${dateRange}`;
      default:
        return `Expense Report${dateRange}`;
    }
  }

  /**
   * Formats date range for display (internal use)
   */
  private static formatDateRangeInternal(dateFrom?: string, dateTo?: string): string {
    if (!dateFrom && !dateTo) return '';
    if (dateFrom && dateTo) return ` (${dateFrom} to ${dateTo})`;
    if (dateFrom) return ` (from ${dateFrom})`;
    if (dateTo) return ` (to ${dateTo})`;
    return '';
  }

  /**
   * Public method to format date range for export service
   */
  static formatDateRange(dateFrom?: string, dateTo?: string): string {
    if (!dateFrom && !dateTo) return 'All dates';
    if (dateFrom && dateTo) return `${dateFrom} to ${dateTo}`;
    if (dateFrom) return `from ${dateFrom}`;
    if (dateTo) return `to ${dateTo}`;
    return 'All dates';
  }

  /**
   * Validates report filters
   * Requirements: 4.5 - Filter validation
   */
  static validateFilters(filters: ReportFilters): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Date validation
    if (filters.dateFrom && filters.dateTo) {
      const fromDate = new Date(filters.dateFrom);
      const toDate = new Date(filters.dateTo);
      
      if (fromDate > toDate) {
        errors.push('Start date must be before end date');
      }
    }

    // Amount validation
    if (filters.minAmount && filters.maxAmount) {
      if (filters.minAmount > filters.maxAmount) {
        errors.push('Minimum amount must be less than maximum amount');
      }
    }

    if (filters.minAmount && filters.minAmount < 0) {
      errors.push('Minimum amount cannot be negative');
    }

    if (filters.maxAmount && filters.maxAmount < 0) {
      errors.push('Maximum amount cannot be negative');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Gets available filter options
   */
  static getFilterOptions() {
    return {
      categories: Object.values(ExpenseCategory),
      statuses: Object.values(ExpenseStatus),
      reportTypes: Object.values(ReportType)
    };
  }

  /**
   * Formats currency amount for display
   */
  static formatAmount(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  /**
   * Formats percentage for display
   */
  static formatPercentage(percentage: number): string {
    return `${percentage.toFixed(1)}%`;
  }
}